import React, { useEffect, useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import ListingFieldChanges from '../../../components/listingDetail/Overview/ListingFieldChanges';
import PhaseASectionReview from '../../../components/listingDetail/Overview/PhaseASectionReview';
import client from '../../../lib/feathers';

// Pull the few human-relevant signals out of the verbose stage-03 import
// proposal text (counts, dedup-care, contamination) so the card can lead with
// a summary instead of a wall of text. (Stage 03 is text today; when it emits a
// structured summary this can read that instead.)
function parse03(text) {
  const out = { contamination: [] };
  if (!text) return out;
  const mw = text.match(/\((\d+) libations \/ (\d+) vintages\)/);
  if (mw) { out.libations = +mw[1]; out.vintages = +mw[2]; }
  const mu = text.match(/(\d+) libation\(s\) on this listing have user data/);
  if (mu) out.userData = +mu[1];
  out.contamination = text.split('\n')
    .filter((l) => l.includes('leads with another producer'))
    .map((l) => {
      const m = l.match(/\s(.+?) — leads with another producer/);
      return m ? m[1].trim() : null;
    })
    .filter(Boolean);
  return out;
}

// Blockers = things that genuinely need a human decision before approving.
// (address_conflict / owner_propose flags are informational — shown in the
// Phase-A panel — and do NOT block, since they're never auto-applied.)
function runBlockers(stages) {
  const blockers = [];
  const pd = stages['05_listing_fields'] && stages['05_listing_fields'].proposalData;
  for (const f of (pd && pd.flags) || []) {
    if (f.type === 'name_mismatch') blockers.push(`Name mismatch: "${f.old}" → "${f.new}"`);
  }
  const vid = stages.verify_identity;
  if (vid && vid.status === 'misbonded') blockers.push(`Identity misbonded (${vid.confidence || '?'})`);
  const s3 = parse03(stages['03_import'] && stages['03_import'].proposal);
  if (s3.contamination.length) {
    blockers.push(`${s3.contamination.length} possible contamination flag(s): ${s3.contamination.slice(0, 3).join(', ')}${s3.contamination.length > 3 ? '…' : ''}`);
  }
  return blockers;
}

/**
 * Pending Approval view — lists full-chain runs that finished their
 * read-only stages and produced a proposed plan, but haven't been
 * approved for the prod-writing stages yet.
 *
 * Each row shows:
 *   - listing name
 *   - when the chain ran
 *   - stage breakdown (01/02/02c always; 03/04 dry-run proposals)
 *   - the proposal text from stage 03 + 04 in a pre block
 *   - "Approve" button that PATCHes approvedAt + approvedByUserId
 *
 * The supervisor's next tick picks up approved rows and re-runs
 * stages 03 + 04 with the prod token. Until approved, the row sits
 * here indefinitely.
 */
export default function ListingDataHealthPendingApproval() {
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      // Just filter on outcome — once an approved row is claimed by
      // the supervisor its outcome flips out of 'awaiting_review' and
      // the row naturally drops out of the result. (Filtering on
      // `approvedAt: null` here breaks: feathers-rest serializes the
      // literal `null` as the string "null" which Postgres can't coerce
      // to a timestamp → "invalid input syntax for type timestamp".)
      //
      // Page through ALL awaiting-review rows — the server caps $limit at 50,
      // so a single fetch silently hid the tail (the queue was >50). Loop on
      // $skip until a short page. Bounded by MAX_PAGES so a runaway queue can't
      // spin forever.
      const PAGE = 50;
      const MAX_PAGES = 40; // 2,000-row safety ceiling
      const all = [];
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const result = await client.service('admin/listing-enrichment-run').find({
          query: {
            outcome: 'awaiting_review',
            // Newest-completed first — a re-run (e.g. after fixing a website or
            // shipping new crawl support) should surface at the TOP for review,
            // not be buried under older rows. (Approved-but-not-yet-claimed rows
            // also sit in awaiting_review; the !approvedAt filter below drops
            // them, and paging through everything means none are hidden.)
            $sort: { finishedAt: -1 },
            $limit: PAGE,
            $skip: page * PAGE,
          },
        });
        const batch = result.data || [];
        all.push(...batch);
        if (batch.length < PAGE) break;
      }
      // Filter out already-approved rows on the client. They stay in
      // outcome='awaiting_review' until the supervisor's next tick
      // re-claims them (~5-15 min) and would otherwise clutter the
      // "needs your eyes" view. (Can't filter server-side with
      // `approvedAt: null` — feathers-rest serializes null as the
      // string "null" and Postgres 500s on timestamp coercion.)
      const unapproved = all.filter((r) => !r.approvedAt);
      setRuns(unapproved);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load pending-approval queue');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (runId) => {
    if (!window.confirm('Approve this run? The supervisor will write the proposed changes to prod on its next tick.')) return;
    setBusyId(runId);
    try {
      // Server stamps approvedByUserId from auth context — we just
      // send the timestamp. Avoids the prod bug where me.id from
      // Redux was sometimes undefined and the patch silently no-op'd.
      const patched = await client.service('admin/listing-enrichment-run').patch(runId, {
        approvedAt: new Date().toISOString(),
      });
      // Verify the server actually persisted the approval before
      // removing from the list. If approvedAt didn't stick, surface
      // an error instead of pretending it worked.
      if (!patched || !patched.approvedAt) {
        throw new Error('approvedAt did not persist on the server — patch was a no-op');
      }
      setRuns((cur) => (cur || []).filter((r) => r.id !== runId));
    } catch (e) {
      alert(`Approve failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  // Per-phase approve: apply just listing fields (A) or just wines (B). The
  // supervisor runs only the approved phase. A row may have one phase
  // auto-approved already; this lets the operator action the other.
  const approvePhase = async (runId, phase) => {
    const label = phase === 'A' ? 'listing fields' : 'wines';
    if (!window.confirm(`Apply ${label} for this run? The supervisor will write them to prod on its next tick.`)) return;
    setBusyId(runId);
    try {
      const field = phase === 'A' ? 'phaseAApprovedAt' : 'phaseBApprovedAt';
      const patched = await client.service('admin/listing-enrichment-run').patch(runId, {
        [field]: new Date().toISOString(),
      });
      if (!patched || !patched[field]) {
        throw new Error(`${field} did not persist — patch was a no-op`);
      }
      setRuns((cur) => (cur || []).filter((r) => r.id !== runId));
    } catch (e) {
      alert(`Apply ${label} failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  // Reject keeps the proposal text on the row for audit but moves the
  // row out of awaiting_review so it stops cluttering the queue. The
  // outcome flips to 'skipped' with an explanatory error. (Not adding
  // a new 'rejected' enum value — overloaded 'skipped' works fine and
  // avoids a migration.)
  const reject = async (runId) => {
    const reason = window.prompt('Reject this proposal? Add a short reason (will be saved on the row):', 'admin rejected');
    if (reason === null) return;  // operator cancelled the prompt
    setBusyId(runId);
    try {
      await client.service('admin/listing-enrichment-run').patch(runId, {
        outcome: 'skipped',
        error: `admin rejected: ${reason || 'no reason given'}`,
        finishedAt: new Date().toISOString(),
      });
      setRuns((cur) => (cur || []).filter((r) => r.id !== runId));
    } catch (e) {
      alert(`Reject failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Reports', href: '/admin/reports' },
            { name: 'Listing Data Health', href: '/admin/reports/listing-data-health' },
            { name: 'Pending Approval' },
          ]} />
        </nav>
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-3">Pending Approval</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          Full-chain runs that finished their read-only stages (crawl / extract / normalize) and produced a proposed plan for stages 03 + 04 (the prod-writing ones). Review the proposal and click <strong>Approve</strong> to let the supervisor run the writes on its next tick. The row stays here indefinitely until approved.
        </p>
      </div>

      {error && <div className="text-sm text-red-700 mb-4">{error}</div>}
      {!error && runs === null && <div className="text-sm text-gray-500">Loading…</div>}
      {!error && runs && runs.length === 0 && (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500 italic">
          Nothing waiting for approval. New full-chain runs land here after their read-only stages complete.
        </div>
      )}

      {!error && runs && runs.length > 0 && (() => {
        // Review-first: runs with blockers float to the top.
        const decorated = runs.map((r) => {
          const stages = (r.stages && typeof r.stages === 'object') ? r.stages : {};
          return { r, stages, blockers: runBlockers(stages) };
        });
        decorated.sort((a, b) => b.blockers.length - a.blockers.length);
        const needCount = decorated.filter((d) => d.blockers.length > 0).length;
        const cleanCount = decorated.length - needCount;
        return (
        <>
        <div className="text-sm text-gray-600 mb-3">
          <span className="font-medium text-amber-700">{needCount} need review</span>
          <span className="mx-2 text-gray-300">·</span>
          <span className="text-green-700">{cleanCount} look clean</span>
        </div>
        <div className="space-y-4">
          {decorated.map(({ r, stages, blockers }) => {
            const proposal03 = stages['03_import']?.proposal;
            const proposal04 = stages['04_upload_assets']?.proposal;
            const listingChanges = stages['05_listing_fields']?.proposalData;
            const s3 = parse03(proposal03);
            const phaseACount = listingChanges
              ? (listingChanges.fills?.length || 0) + (listingChanges.overwrites?.length || 0)
              : 0;
            const clean = blockers.length === 0;
            return (
              <div key={r.id} className={`bg-white shadow rounded-lg overflow-hidden border-l-4 ${clean ? 'border-green-400' : 'border-amber-400'}`}>
                <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900">
                      {r.listing ? (
                        <NavLink to={`/admin/listings/${r.listing.id}`} className="text-indigo-700 hover:underline">
                          {r.listing.name}
                        </NavLink>
                      ) : (
                        <span className="text-gray-400 italic">listing unknown</span>
                      )}
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-gray-500">
                      <span>Ran {r.finishedAt ? formatDistanceToNow(new Date(r.finishedAt), { addSuffix: true }) : '—'}</span>
                      <span>· trigger {r.triggeredBy}</span>
                      <NavLink to={`/admin/reports/listing-data-health/run/${r.id}`} className="text-indigo-600 hover:underline">· steps →</NavLink>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button type="button" onClick={() => reject(r.id)} disabled={busyId === r.id}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      title="Drop this proposed run without writing.">
                      Reject
                    </button>
                    {/* Legacy all-or-nothing Phase-A apply — only for old runs
                        without category sections; new runs use the per-category
                        panel below. */}
                    {!listingChanges?.sections?.length && (
                      <button type="button" onClick={() => approvePhase(r.id, 'A')} disabled={busyId === r.id}
                        className="inline-flex items-center px-3 py-2 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white disabled:opacity-50"
                        title="Apply only the listing fields (address / hours / website / socials).">
                        Apply listing fields
                      </button>
                    )}
                    <button type="button" onClick={() => approvePhase(r.id, 'B')} disabled={busyId === r.id}
                      className="inline-flex items-center px-3 py-2 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white disabled:opacity-50"
                      title="Apply only the wines (libations + vintages + assets).">
                      Apply wines
                    </button>
                    <button type="button" onClick={() => approve(r.id)} disabled={busyId === r.id}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50">
                      {busyId === r.id ? 'Approving…' : 'Approve both'}
                    </button>
                  </div>
                </div>

                {/* Verdict + at-a-glance summary — the part you actually scan */}
                <div className="px-4 py-3">
                  {clean ? (
                    <div className="text-sm font-medium text-green-700">✓ Looks clean — safe to approve</div>
                  ) : (
                    <div>
                      <div className="text-sm font-semibold text-amber-800 mb-1">⚠ Needs review</div>
                      <ul className="list-disc list-inside text-sm text-amber-900 space-y-0.5">
                        {blockers.map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                    {s3.libations != null && <span>📦 {s3.libations} wines · {s3.vintages} vintages</span>}
                    {s3.userData ? <span className="text-gray-500">👥 {s3.userData} with user data (dedup care)</span> : null}
                    {phaseACount ? <span>📝 Phase A: {phaseACount} field change(s)</span> : null}
                    {!listingChanges && <span className="text-gray-400 italic">no Phase-A data (pre-fix run)</span>}
                  </div>
                </div>

                {/* Phase-A listing fields. New runs carry category `sections`
                    → interactive per-category accept; older runs fall back to
                    the read-only view + the all-or-nothing button above. */}
                {listingChanges && (
                  <div className="px-4 pb-3">
                    {listingChanges.sections?.length ? (
                      <PhaseASectionReview
                        run={r}
                        sections={listingChanges.sections}
                        hasWines={(s3.libations || 0) > 0}
                        onDone={(runId, cleared) => {
                          if (cleared) setRuns((cur) => (cur || []).filter((x) => x.id !== runId));
                        }}
                      />
                    ) : (
                      <ListingFieldChanges proposalData={listingChanges} />
                    )}
                  </div>
                )}

                {/* Verbose stage 03/04 proposals — collapsed (open by default only when review needed) */}
                <details open={!clean} className="px-4 py-2 border-t border-gray-100">
                  <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">Full proposal (stage 03 import / 04 assets)</summary>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Stage 03 — Import wines</h4>
                      {proposal03 ? (
                        <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-64">{proposal03}</pre>
                      ) : <div className="text-xs text-gray-400 italic">no proposal captured</div>}
                    </div>
                    <div>
                      <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Stage 04 — Upload assets</h4>
                      {proposal04 ? (
                        <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-64">{proposal04}</pre>
                      ) : <div className="text-xs text-gray-400 italic">no proposal captured</div>}
                    </div>
                  </div>
                </details>

                {/* Stage breakdown chips */}
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs flex flex-wrap gap-2">
                  {Object.entries(stages).map(([k, v]) => (
                    <span key={k} className={`px-2 py-0.5 rounded font-mono ${v?.ok === true ? 'bg-green-50 text-green-700' : v?.ok === false ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {k} {v?.ok === true ? '✓' : v?.ok === false ? '✗' : '?'}{v?.durationSec != null ? ` ${v.durationSec}s` : ''}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        </>
        );
      })()}
    </Page>
  );
}
